/**
 * The station-volumes half of a run dialog, as pure functions.
 *
 * Spec §10.4: one component, one zod fragment, one estimator, shared by
 * `RunSurfaceDifferenceDialog` and `RunDeviationAnalysisDialog`. Everything a
 * dialog needs in order to render, validate, block and submit the station
 * section lives here; the JSX lives in `@/components/StationVolumesFields`.
 * Nothing in this module touches React, jotai or i18n, so both dialogs can
 * compute the same answers and cannot disagree about what is blocking.
 */
import { z } from 'zod';

import { displayToInternal, formatStation, internalToDisplay, parseStation, type Alignment } from '@/core/alignment';
import type { StationVolumeInput } from '@/graphql/graphql';
import { toAlignmentInput } from '@/lib/stationVolumes/alignmentInput';

export const INTERVAL_PRESETS = [5, 10, 20, 25, 50];
export const MAX_BINS = 20_000;
export const WARN_BINS = 5_000;
/**
 * Cap on the two cross-section byproducts together: `station_offsets.f32`
 * (three float32 planes over `nStations × nOffsets`, 12 bytes a cell — the bin
 * means) plus `station_sections.f32` (two planes over
 * `(nStations + 1) × nOffsets`, 8 bytes a cell — the true station-line
 * sections). The bin count and the swath are capped separately but their
 * PRODUCT was not, so 20 000 bins across 1 000 m of swath is a legal request
 * for a multi-gigabyte download. Same number and same formula in the resolver
 * (`stationVolumesValidation.ts`) and the worker
 * (`station_bins.load_station_input`).
 */
export const MAX_SECTION_BYTES = 64 * 1024 * 1024;
/** MiB, one decimal — the unit the over-limit message is written in. */
export const formatMiB = (bytes: number): string => (bytes / (1024 * 1024)).toFixed(1);

/** Bin count, corridor area and section-array size for the live estimate line. */
export function estimateStationVolumes({
  length,
  interval,
  swathLeft,
  swathRight,
  from,
  to,
  offsetBin,
  resolution,
}: {
  length: number;
  interval: number;
  swathLeft: number;
  swathRight: number;
  from: number;
  to: number;
  offsetBin: number;
  /** Analysis grid resolution (m) — the worker clamps the offset bin up to
   *  this (spec L164), so the estimate must match what the resolver will
   *  actually produce, not the raw requested offset bin (fix round 1, item 3). */
  resolution: number;
}) {
  const lo = Math.max(0, Math.min(from, length));
  const hi = Math.max(lo, Math.min(to, length));
  const span = hi - lo;
  const binCount = interval > 0 ? Math.max(0, Math.ceil(span / interval - 1e-9)) : 0;
  const width = Math.max(0, swathLeft) + Math.max(0, swathRight);
  const effOffsetBin = Math.max(offsetBin, resolution);
  const nOffsets = effOffsetBin > 0 ? Math.max(1, Math.ceil(width / effOffsetBin - 1e-9)) : 1;
  return {
    binCount,
    /** Station span actually covered — used to detect "range shorter than one interval" (spec R11: `to − from ≥ interval`). */
    span,
    corridorAreaM2: span * width,
    nOffsets,
    /** The offset bin the worker will actually use, after the resolution clamp. */
    effOffsetBin,
    sectionBytes: binCount * nOffsets * 12 + (binCount + 1) * nOffsets * 8,
  };
}

/** `sectionBytes` for humans: kB below 1 MB, MB above (fix round 1, item 14). */
export function formatSectionBytes(bytes: number): string {
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} kB`;
}

/**
 * Display text → INTERNAL station, distinguishing *why* a value doesn't
 * resolve (fix round 1, item 4) instead of collapsing every failure into a
 * single "ambiguous" message:
 *  - text doesn't parse as a station at all → `'unparseable'`
 *  - text parses but falls outside `[0, length]` → `'outOfRange'`
 *  - text parses and is in range, but a station equation's back-shift opens
 *    a gap that swallows it → `'ambiguous'`. `displayToInternal` can only
 *    return `null` for that reason when the alignment actually has
 *    equations — on an equation-free alignment, `null` always means
 *    out-of-range.
 */
export type StationParseResult =
  | { kind: 'ok'; value: number }
  | { kind: 'unparseable' }
  | { kind: 'outOfRange' }
  | { kind: 'ambiguous' };

/**
 * Half of the quantum `formatStation` prints at (two decimals), so a station
 * the UI SHOWS is only ever this far from the one it means.
 *
 * Every station in the dialog is printed rounded to nearest — the "Full
 * length" button, the From/To placeholders, and the bounds quoted in the
 * out-of-range message all go through `formatStation`. On an alignment
 * 800.036 m long that prints "STA 0+800.04", which parses back to a station 4
 * mm PAST the end: the button filled in a value its own validator then
 * refused, quoting the same number back at the user. Accepting the printed
 * endpoint and clamping it to the real one is the fix; anything further out
 * than the printing could explain is still refused.
 */
const STATION_DISPLAY_EPS_M = 0.005;

export function parseStationRange(alignment: Alignment, text: string, fallback: number): StationParseResult {
  if (!text.trim()) return { kind: 'ok', value: fallback };
  const display = parseStation(text);
  if (display == null) return { kind: 'unparseable' };
  const internal = displayToInternal(alignment, display);
  if (internal != null) return { kind: 'ok', value: internal };
  // Only the two ENDPOINTS are rescued, and only against their own printed
  // form: a station lost in an equation gap is still ambiguous, and one past
  // the end by more than the printing could explain is still out of range.
  for (const endpoint of [0, alignment.length]) {
    if (Math.abs(display - internalToDisplay(alignment, endpoint)) <= STATION_DISPLAY_EPS_M)
      return { kind: 'ok', value: endpoint };
  }
  return alignment.equations.length > 0 ? { kind: 'ambiguous' } : { kind: 'outOfRange' };
}

/** SDL default for `StationVolumeInput.minCoveragePct`. */
export const DEFAULT_MIN_COVERAGE_PCT = 60;

/**
 * Crossfall defaults (crossfall spec §3, assumptions A-1 and A-2). 0.5 m in
 * from the centreline clears the crown itself — the one place a fitted line
 * is meaningless — and 3.5 m out is a lane. ±0.3 % absolute is what a
 * pavement spec typically allows on a 2.5 % design crossfall.
 */
export const DEFAULT_CROSSFALL_FROM_M = 0.5;
export const DEFAULT_CROSSFALL_TO_M = 3.5;
export const DEFAULT_CROSSFALL_TOLERANCE_PCT = 0.3;
/** Same three numbers as `scripts/crossfall.py`'s MIN_BAND_WIDTH_M /
 *  MIN_TOLERANCE_PCT / MAX_TOLERANCE_PCT, and as the worker's
 *  `load_station_input` validation. A band narrower than this is two offset
 *  columns, and two points fit any line exactly. */
export const MIN_CROSSFALL_BAND_WIDTH_M = 1;
export const MIN_CROSSFALL_TOLERANCE_PCT = 0.05;
export const MAX_CROSSFALL_TOLERANCE_PCT = 5;

/**
 * Why one side's band is refused, or `null` when it is fine. Three separate
 * reasons rather than one boolean because they have three different fixes:
 * `'band'` is a pair the wrong way round (or a negative offset), `'width'` is
 * a band the fit cannot use, and `'swath'` is a band the section has no
 * columns for. Folding them together told a user with a legal 3 m band in a
 * 3 m swath that their band was "too narrow", which is someone else's bug.
 */
export type CrossfallBandIssue = 'band' | 'width' | 'swath';

/** The one place the three band rules live: `refineStationVolumes` (which
 *  turns them into issue tags) and `deriveStationVolumes` (which turns them
 *  into an inline message) both read it, so the message under the control and
 *  the refusal to submit can never disagree. Same three rules, in the same
 *  order, as `station_bins.load_station_input`. */
export function crossfallBandIssue(from: number, to: number, swath: number): CrossfallBandIssue | null {
  if (!(from >= 0) || !(to > from)) return 'band';
  if (to - from < MIN_CROSSFALL_BAND_WIDTH_M) return 'width';
  // Against THIS side's swath: the left band lives at offsets −to … −from.
  if (to > swath) return 'swath';
  return null;
}

/**
 * Floor on the deviation path's STATION grid (spec §10.2 step 2). Deviation's
 * volume grid defaults to 5 cm — right for a wall panel, hopeless for a
 * corridor-scale pass — so station volumes bin at
 * `max(volumeResolution, 0.25 m)`, and the dialog's estimate has to be sized
 * on the same number the resolver and the worker use, or the live "≈ N bins ·
 * … of cross-section data" line describes a run nobody is going to make.
 *
 * Same number in `scripts/deviation.py::STATION_GRID_MIN_RES` and
 * `server/api/.../mutation/stationVolumesValidation.ts::STATION_GRID_MIN_RES`.
 */
export const STATION_GRID_MIN_RES = 0.25;

export function stationGridResolution(volumeResolutionM: number | null | undefined): number {
  const v = Number(volumeResolutionM);
  return Number.isFinite(v) && v > 0 ? Math.max(v, STATION_GRID_MIN_RES) : STATION_GRID_MIN_RES;
}

export interface StationVolumesFormValues {
  stationVolumesEnabled: boolean;
  alignmentKey: string;
  stationInterval: number;
  swathLeft: number;
  swathRight: number;
  swathLinked: boolean;
  stationFromText: string;
  stationToText: string;
  offsetBin: number;
  /** The crossfall check. Its six fields below are ignored — and hidden —
   *  while this is false, exactly as the station fields are while
   *  `stationVolumesEnabled` is. */
  crossfallEnabled: boolean;
  /** Per-side include switches. Either band may be omitted (spec §3, and the
   *  SDL declares `left`/`right` independently optional), which is the only
   *  way to run a check on a one-sided corridor — `swathLeft: 0` is legal, and
   *  no band fits in it. At least one has to stay on. */
  crossfallLeftEnabled: boolean;
  crossfallRightEnabled: boolean;
  crossfallLeftFrom: number;
  crossfallLeftTo: number;
  crossfallRightFrom: number;
  crossfallRightTo: number;
  /** UI affordance only, never sent: the two bands are mirrored on edit. */
  crossfallLinked: boolean;
  crossfallTolerancePct: number;
}

/**
 * Pre-fill shape for the station-volumes section (T9.2). Task 10's dock
 * empty-state "Re-run with new parameters" button opens a run dialog with a
 * value built from the previous run's `StationParams` (see
 * `app/src/lib/stationVolumes/types.ts`) so the user doesn't retype
 * everything to correct a swath that was too narrow. `stationFrom`/`stationTo`
 * are INTERNAL (2D) stations — `stationVolumesDefaults` converts them to
 * display text once the target alignment is resolved from `alignmentKey`.
 */
export interface StationVolumesInitialParams {
  alignmentKey: string;
  stationInterval: number;
  swathLeft: number;
  swathRight: number;
  offsetBin: number;
  minCoveragePct: number;
  stationFrom?: number;
  stationTo?: number;
  /** The previous run's `params.crossfall`, so "Re-run with new parameters"
   *  carries the check the way it carries the swath. `null`/absent on a run
   *  that had none, which starts the disclosure closed. */
  crossfall?: {
    left?: { fromM: number; toM: number } | null;
    right?: { fromM: number; toM: number } | null;
    tolerancePct: number;
  } | null;
}

/**
 * `defaultValues` for the station half of either dialog's form.
 *
 * `initial` is the dock's "Re-run with new parameters" pre-fill; its
 * `stationFrom`/`stationTo` are INTERNAL stations and need the target
 * alignment to become the display text the inputs hold, so when the alignment
 * is not (yet) registered the text fields simply start empty and the
 * full-length placeholders show.
 *
 * `gateAllowed` gates the *default* as well as the section: the dock always
 * supplies `initial`, and without this a locked user would land on a fully
 * live, submittable station section.
 */
export function stationVolumesDefaults(
  initial: StationVolumesInitialParams | undefined,
  alignments: Map<string, Alignment>,
  gateAllowed: boolean,
): StationVolumesFormValues {
  const a = initial ? alignments.get(initial.alignmentKey) : undefined;
  const text = (internal: number | undefined) =>
    internal != null && a ? formatStation(internalToDisplay(a, internal)) : '';
  return {
    stationVolumesEnabled: !!initial && gateAllowed,
    alignmentKey: initial?.alignmentKey ?? '',
    stationInterval: initial?.stationInterval ?? 20,
    swathLeft: initial?.swathLeft ?? 15,
    swathRight: initial?.swathRight ?? 15,
    swathLinked: initial ? initial.swathLeft === initial.swathRight : true,
    stationFromText: text(initial?.stationFrom),
    stationToText: text(initial?.stationTo),
    offsetBin: initial?.offsetBin ?? 0.5,
    // A re-run carries the previous check. `left`/`right` are independently
    // optional server-side, so a one-sided run pre-fills the missing side
    // with the default band rather than a zero — a zero-width band is the one
    // value the refinement below rejects outright.
    crossfallEnabled: !!initial?.crossfall && gateAllowed,
    // A run that measured one side comes back as one side. The omitted side
    // keeps the DEFAULT band behind its switch rather than a zero, so ticking
    // it on is one click and never a zero-width band.
    crossfallLeftEnabled: initial?.crossfall ? initial.crossfall.left != null : true,
    crossfallRightEnabled: initial?.crossfall ? initial.crossfall.right != null : true,
    crossfallLeftFrom: initial?.crossfall?.left?.fromM ?? DEFAULT_CROSSFALL_FROM_M,
    crossfallLeftTo: initial?.crossfall?.left?.toM ?? DEFAULT_CROSSFALL_TO_M,
    crossfallRightFrom: initial?.crossfall?.right?.fromM ?? DEFAULT_CROSSFALL_FROM_M,
    crossfallRightTo: initial?.crossfall?.right?.toM ?? DEFAULT_CROSSFALL_TO_M,
    // A one-sided pre-fill starts UNLINKED: there is nothing to mirror onto,
    // and a link left on would silently rewrite a band the user cannot see.
    crossfallLinked:
      initial?.crossfall == null ||
      (initial.crossfall.left != null &&
        initial.crossfall.right != null &&
        initial.crossfall.left.fromM === initial.crossfall.right.fromM &&
        initial.crossfall.left.toM === initial.crossfall.right.toM),
    crossfallTolerancePct: initial?.crossfall?.tolerancePct ?? DEFAULT_CROSSFALL_TOLERANCE_PCT,
  };
}

/**
 * The station half of a dialog's zod object, to spread into `z.object({…})`.
 *
 * Bounds on the four numeric fields are NOT declared here: they are enforced
 * in `refineStationVolumes`, gated on `stationVolumesEnabled`. An
 * unconditional `.min()/.max()` would reject the WHOLE form — including the
 * main analysis — over a stale, hidden station value left behind when the
 * user unticked the section.
 */
export const stationVolumesZodShape = {
  stationVolumesEnabled: z.boolean(),
  alignmentKey: z.string(),
  stationInterval: z.number(),
  swathLeft: z.number(),
  swathRight: z.number(),
  swathLinked: z.boolean(),
  stationFromText: z.string(),
  stationToText: z.string(),
  offsetBin: z.number(),
  crossfallEnabled: z.boolean(),
  crossfallLeftEnabled: z.boolean(),
  crossfallRightEnabled: z.boolean(),
  crossfallLeftFrom: z.number(),
  crossfallLeftTo: z.number(),
  crossfallRightFrom: z.number(),
  crossfallRightTo: z.number(),
  crossfallLinked: z.boolean(),
  crossfallTolerancePct: z.number(),
};

/** The `superRefine` body. Issue `message`s are TAGS, not copy — each dialog
 *  maps them to translated strings in its own `onInvalid`. */
export function refineStationVolumes(d: StationVolumesFormValues, ctx: z.RefinementCtx): void {
  // Everything below only matters while the station-volumes section is
  // active — its fields are ignored (and hidden) otherwise.
  if (!d.stationVolumesEnabled) return;
  if (!d.alignmentKey) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['alignmentKey'], message: 'alignmentRequired' });
  }
  if (d.swathLeft + d.swathRight <= 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['swathLeft'], message: 'swathZero' });
  }
  if (d.stationInterval < 1 || d.stationInterval > 500) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['stationInterval'], message: 'intervalRange' });
  }
  if (d.swathLeft < 0 || d.swathLeft > 500) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['swathLeft'], message: 'swathRange' });
  }
  if (d.swathRight < 0 || d.swathRight > 500) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['swathRight'], message: 'swathRange' });
  }
  if (d.offsetBin < 0.1 || d.offsetBin > 5) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['offsetBin'], message: 'offsetBinRange' });
  }
  // The crossfall check, gated on its own switch inside the station section:
  // a stale band left behind by an unticked disclosure must not block a run.
  if (!d.crossfallEnabled) return;
  // A check with no side at all is refused, never a silent no-op — the same
  // rule as the worker's "crossfall needs at least one band".
  if (!d.crossfallLeftEnabled && !d.crossfallRightEnabled) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['crossfallEnabled'], message: 'crossfallNoBand' });
  }
  const TAG = { band: 'crossfallBand', width: 'crossfallWidth', swath: 'crossfallSwath' } as const;
  const band = (from: number, to: number, swath: number, path: 'crossfallLeftTo' | 'crossfallRightTo') => {
    const issue = crossfallBandIssue(from, to, swath);
    // A stale band behind an unticked SIDE must not block a run, exactly as a
    // stale station field behind an unticked section does not.
    if (issue) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: TAG[issue] });
  };
  if (d.crossfallLeftEnabled) band(d.crossfallLeftFrom, d.crossfallLeftTo, d.swathLeft, 'crossfallLeftTo');
  if (d.crossfallRightEnabled) band(d.crossfallRightFrom, d.crossfallRightTo, d.swathRight, 'crossfallRightTo');
  if (
    !(d.crossfallTolerancePct >= MIN_CROSSFALL_TOLERANCE_PCT) ||
    d.crossfallTolerancePct > MAX_CROSSFALL_TOLERANCE_PCT
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['crossfallTolerancePct'],
      message: 'crossfallTolerance',
    });
  }
}

export interface StationVolumesDerived {
  alignment: Alignment | undefined;
  estimate: ReturnType<typeof estimateStationVolumes> | null;
  fromInternal: number;
  toInternal: number;
  rangeInvalidKind: 'unparseable' | 'outOfRange' | 'ambiguous' | null;
  rangeInvalidText: string;
  rangeInvalid: boolean;
  rangeTooShort: boolean;
  tooManyBins: boolean;
  sectionTooBig: boolean;
  offsetBinClamped: boolean;
  intervalOutOfBounds: boolean;
  swathLeftOutOfBounds: boolean;
  swathRightOutOfBounds: boolean;
  offsetBinOutOfBounds: boolean;
  /** Per-side band problems — WHICH rule was broken, so the disclosure prints
   *  the message about the user's actual problem — plus the tolerance and the
   *  no-side-at-all cases. All four are `null`/`false` while the crossfall
   *  check, or the station section around it, is off: the invariant lives in
   *  the value, not in every caller. */
  crossfallLeftIssue: CrossfallBandIssue | null;
  crossfallRightIssue: CrossfallBandIssue | null;
  crossfallToleranceInvalid: boolean;
  crossfallNoBand: boolean;
  /** True when Run must stay disabled because of the station section. */
  blocked: boolean;
}

/** Everything the section renders and the Run button reads, in one pure
 *  function so the two dialogs cannot disagree about what is blocking. */
export function deriveStationVolumes(
  alignment: Alignment | undefined,
  values: StationVolumesFormValues,
  resolution: number,
): StationVolumesDerived {
  const { stationInterval: interval, swathLeft, swathRight, offsetBin } = values;
  // Distinguish *why* a from/to station doesn't resolve (fix round 1, item 4)
  // instead of a single catch-all "ambiguous" — see `parseStationRange`.
  const fromResult: StationParseResult = alignment
    ? parseStationRange(alignment, values.stationFromText, 0)
    : { kind: 'ok', value: 0 };
  const toResult: StationParseResult = alignment
    ? parseStationRange(alignment, values.stationToText, alignment.length)
    : { kind: 'ok', value: 0 };
  const fromInternal = fromResult.kind === 'ok' ? fromResult.value : 0;
  const toInternal = toResult.kind === 'ok' ? toResult.value : 0;
  const rangeInvalidKind: 'unparseable' | 'outOfRange' | 'ambiguous' | null =
    fromResult.kind !== 'ok' ? fromResult.kind : toResult.kind !== 'ok' ? toResult.kind : null;
  const rangeInvalidText = fromResult.kind !== 'ok' ? values.stationFromText : values.stationToText;
  const rangeInvalid = rangeInvalidKind !== null;

  const estimate =
    alignment && !rangeInvalid
      ? estimateStationVolumes({
          length: alignment.length,
          interval,
          swathLeft,
          swathRight,
          from: fromInternal,
          to: toInternal,
          offsetBin,
          resolution,
        })
      : null;

  // Blocks exactly what the resolver rejects (spec R11: `to − from ≥ interval`).
  const rangeTooShort = !!estimate && estimate.span < interval;
  const tooManyBins = !!estimate && estimate.binCount > MAX_BINS;
  const sectionTooBig = !!estimate && estimate.sectionBytes > MAX_SECTION_BYTES;
  const offsetBinClamped = !!estimate && estimate.effOffsetBin > offsetBin;
  const intervalOutOfBounds = interval < 1 || interval > 500;
  const swathLeftOutOfBounds = swathLeft < 0 || swathLeft > 500;
  const swathRightOutOfBounds = swathRight < 0 || swathRight > 500;
  const offsetBinOutOfBounds = offsetBin < 0.1 || offsetBin > 5;
  // Gated on BOTH switches, so an unticked station section (or an unticked
  // check) can never leave a live flag behind for a caller to remember to
  // ignore. `crossfallBandIssue` is the same function the refinement uses.
  const crossfallOn = values.stationVolumesEnabled && values.crossfallEnabled;
  const crossfallLeftIssue =
    crossfallOn && values.crossfallLeftEnabled
      ? crossfallBandIssue(values.crossfallLeftFrom, values.crossfallLeftTo, swathLeft)
      : null;
  const crossfallRightIssue =
    crossfallOn && values.crossfallRightEnabled
      ? crossfallBandIssue(values.crossfallRightFrom, values.crossfallRightTo, swathRight)
      : null;
  const crossfallToleranceInvalid =
    crossfallOn &&
    (!(values.crossfallTolerancePct >= MIN_CROSSFALL_TOLERANCE_PCT) ||
      values.crossfallTolerancePct > MAX_CROSSFALL_TOLERANCE_PCT);
  const crossfallNoBand = crossfallOn && !values.crossfallLeftEnabled && !values.crossfallRightEnabled;

  return {
    alignment,
    estimate,
    fromInternal,
    toInternal,
    rangeInvalidKind,
    rangeInvalidText,
    rangeInvalid,
    rangeTooShort,
    tooManyBins,
    sectionTooBig,
    offsetBinClamped,
    intervalOutOfBounds,
    swathLeftOutOfBounds,
    swathRightOutOfBounds,
    offsetBinOutOfBounds,
    crossfallLeftIssue,
    crossfallRightIssue,
    crossfallToleranceInvalid,
    crossfallNoBand,
    blocked:
      values.stationVolumesEnabled &&
      (!alignment ||
        rangeInvalid ||
        rangeTooShort ||
        tooManyBins ||
        sectionTooBig ||
        swathLeft + swathRight <= 0 ||
        intervalOutOfBounds ||
        swathLeftOutOfBounds ||
        swathRightOutOfBounds ||
        crossfallLeftIssue !== null ||
        crossfallRightIssue !== null ||
        crossfallToleranceInvalid ||
        crossfallNoBand ||
        offsetBinOutOfBounds),
  };
}

/** The mutation payload, or `undefined` for "do not send the block". The
 *  caller must have already checked the feature gate — the gate can lapse
 *  while the dialog is open and the mutation costs real compute, so the last
 *  word belongs to the call site, not to a stale form flag. */
export function stationVolumesPayload(
  values: StationVolumesFormValues,
  derived: StationVolumesDerived,
  minCoveragePct: number,
): StationVolumeInput | undefined {
  if (!values.stationVolumesEnabled || !derived.alignment) return undefined;
  return {
    // PLAN: station volumes (deviation, surface difference and the layer
    // stack, which all come through here) station in 2D and take every
    // elevation from the compared surfaces, so the fingerprint covers the
    // horizontal geometry only — see `AlignmentHashMode`.
    alignment: toAlignmentInput(derived.alignment, 'PLAN'),
    stationInterval: values.stationInterval,
    swathLeft: values.swathLeft,
    swathRight: values.swathRight,
    stationFrom: derived.fromInternal,
    stationTo: derived.toInternal,
    offsetBin: values.offsetBin,
    minCoveragePct,
    // Omitted, never null, when the check is off — the same absence
    // convention the worker's `load_station_input` reads (`crossfall is
    // None` means "no check", and a present-but-empty block is an error).
    // A side that is not being measured is OMITTED, not sent as a zero band:
    // `load_station_input` reads a missing side as "no band here" and refuses
    // a zero-width one.
    ...(values.crossfallEnabled
      ? {
          crossfall: {
            ...(values.crossfallLeftEnabled
              ? { left: { fromM: values.crossfallLeftFrom, toM: values.crossfallLeftTo } }
              : {}),
            ...(values.crossfallRightEnabled
              ? { right: { fromM: values.crossfallRightFrom, toM: values.crossfallRightTo } }
              : {}),
            tolerancePct: values.crossfallTolerancePct,
          },
        }
      : {}),
  };
}
